import React from 'react';
import _ from 'lodash';

import {
  Partner_ReturnAuthorizationActionsWidgetDocument,
  Partner_ReturnAuthorizationLifeCycleWidgetDocument,
  ReceiveReturnAuthorizationLineItemInput,
  ReturnAuthorizationsActivity_TabBarDocument,
  useReceiveReturnAuthorizationModalQuery,
  useReceiveReturnAuthorizationMutation,
  useSaveItemReceiptMutation
} from 'generated/graphql';

import Big from 'big.js';
import * as types from './types';



export interface ILineItem {
  id: string;
  label: string;
}

export interface IData {
  itemReceiptId: string;
  lineItems: ILineItem[];
  formState: types.IFormState;
}

export function useQuery(returnAuthorizationId: string) {
  const tuple = useReceiveReturnAuthorizationModalQuery({
    variables: {
      returnAuthorizationId
    }
  });

  return React.useMemo(
    () => {
      const duplicatedLines = _.map(
        tuple.data?.returnAuthorization?.lineItems ?? [],
        lineItem => _.times(lineItem.quantity, () => lineItem)
      );

      const receiptLineItems = _.map(_.flatten(duplicatedLines), (lineItem): types.IReceiptLine => ({
        returnAuthorizationLineItemId: lineItem.id,
        productName: lineItem.product.name,
        refundAmountDollars: new Big(lineItem.refundAmountCents).div(lineItem.quantity).div(100).toFixed(2),
        isReceived: false,
        serialNumber: lineItem.serialNumber
      }));

      const rma = tuple.data?.returnAuthorization;
      const data: IData = {
        itemReceiptId: tuple.data?.returnAuthorization?.itemReceipt?.id ?? '',
        lineItems: _.map(rma?.lineItems ?? [], (li): ILineItem => ({
          id: li.id,
          label: li.product.sku
        })),
        formState: {
          lineItems: receiptLineItems
        }
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}



export function useReceiveReturnAuthorization(returnAuthorizationId: string, storeId?: string) {
  const [fnMutate, tuple] = useReceiveReturnAuthorizationMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: Partner_ReturnAuthorizationLifeCycleWidgetDocument,
        variables: {
          returnAuthorizationId,
          queryAsCustomer: false
        }
      },
      {
        query: Partner_ReturnAuthorizationActionsWidgetDocument,
        variables: { returnAuthorizationId }
      },
      {
        query: ReturnAuthorizationsActivity_TabBarDocument,
        variables: { storeId }
      }
    ]
  });

  const fnWrappedMutate = React.useCallback(
    async (lineItems: types.IReceiptLine[]) => {
      await fnMutate({
        variables: {
          input: {
            returnAuthorizationId,
            receivedLineItems: _.map(lineItems, (lineItem): ReceiveReturnAuthorizationLineItemInput => ({
              returnAuthorizationLineItemId: lineItem.returnAuthorizationLineItemId,
              refundAmountCents: new Big(lineItem.refundAmountDollars).mul(100).toFixed(0),
              inventoryNumber: lineItem.serialNumber
            }))
          }
        }
      });
    },
    [
      fnMutate,
      returnAuthorizationId
    ]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}



export function useSaveItemReceipt(itemReceiptId: string) {
  const [fnMutate, tuple] = useSaveItemReceiptMutation({
    refetchQueries: []
  });

  const fnWrappedMutate = React.useCallback(
    async (formState: types.IFormState) => {
      const filtered = _.filter(formState.lineItems, li => li.isReceived);
      const lineItems = _.map(filtered, (lineItem): ReceiveReturnAuthorizationLineItemInput => ({
        inventoryNumber: lineItem.serialNumber,
        refundAmountCents: new Big(lineItem.refundAmountDollars).mul(100).toString(),
        returnAuthorizationLineItemId: lineItem.returnAuthorizationLineItemId
      }));
      await fnMutate({
        variables: {
          input: {
            id: itemReceiptId,
            lineItems
          }
        }
      });
    },
    [fnMutate, itemReceiptId]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}


